import React, { useEffect, useState } from 'react';
import { TextField, IconButton, Box, Chip } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ILocation } from '../../types/global/helper';
import { showToastMessage } from '../../utils/AppUtils';

/** Match audition union-select list: dark rows, theme accent + dark text on active. */
export type PlacesSuggestionPalette = {
    rowInactiveBg: string;
    rowInactiveColor: string;
    rowActiveBg: string;
    rowActiveColor: string;
    loadingBg: string;
    loadingColor: string;
    /** Optional border around the suggestion list (e.g. `1px solid rgba(...)`). */
    listBorder?: string;
};

interface PlacesAutocompleteFieldProps {
    label: string;
    value: ILocation | ILocation[];
    onChange?: (value: ILocation | ILocation[]) => void;
    onSelect?: (value: ILocation | ILocation[]) => void;
    placeholder?: string;
    fullWidth?: boolean;
    multiple?: boolean;
    textFieldSx?: SxProps<Theme>;
    /** When set, suggestion list + loading row use the same language as MUI Select menus (e.g. union field). */
    suggestionPalette?: PlacesSuggestionPalette;
}

const EMPTY_SINGLE: ILocation = {
    type: 'Point',
    coordinates: [0, 0],
    address: '',
    description: '',
    short_name: '',
};

function firstLocation(value: ILocation | ILocation[]): ILocation | undefined {
    if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : undefined;
    }
    return value || undefined;
}

function hasStoredAddress(value: ILocation | ILocation[]): boolean {
    const loc = firstLocation(value);
    return Boolean(loc?.address?.trim());
}

export const PlacesAutocompleteField: React.FC<PlacesAutocompleteFieldProps> = ({
    label,
    value,
    onChange,
    placeholder = 'Search Places ...',
    fullWidth = true,
    multiple = false,
    onSelect,
    textFieldSx,
    suggestionPalette,
}) => {
    const { themeStyle } = useThemeContext();
    const [textValue, setTextValue] = useState('');

    const handleLocationSelect = (address: string) => {
        setTextValue(address);

        geocodeByAddress(address)
            .then(results => {
                const immediateAddress = results[0].formatted_address;
                return getLatLng(results[0]).then(latLng => ({
                    latLng,
                    immediateAddress,
                }));
            })
            .then(({ latLng, immediateAddress }) => {
                if (latLng.lng && latLng.lat) {
                    const parsedPayload = {
                        type: 'Point' as const,
                        coordinates: [latLng.lng, latLng.lat],
                        address: immediateAddress,
                        description: immediateAddress,
                        short_name: immediateAddress.split(',')[0],
                    };

                    if (multiple) {
                        const currentValues = Array.isArray(value) ? value : [];
                        onChange && onChange([...currentValues, parsedPayload]);
                        setTextValue('');
                    } else {
                        onChange && onChange(parsedPayload);
                    }
                    onSelect && onSelect(parsedPayload);
                } else {
                    showToastMessage('No GeoKeys', 'warning');
                }
            })
            .catch(error => console.error('Error', error));
    };

    const handleRemoveLocation = (indexToRemove: number) => {
        if (multiple && Array.isArray(value)) {
            const newLocations = value.filter((_, index) => index !== indexToRemove);
            onChange && onChange(newLocations);
            onSelect && onSelect(newLocations);
        }
    };

    const clearSingle = () => {
        setTextValue('');
        onChange && onChange(EMPTY_SINGLE);
        onSelect && onSelect(EMPTY_SINGLE);
    };

    useEffect(() => {
        if (!multiple && value) {
            const loc = firstLocation(value);
            setTextValue(loc?.address || '');
        } else if (multiple) {
            setTextValue('');
        }
    }, [value, multiple]);

    const showClearButton =
        Boolean(textValue) ||
        (!multiple && hasStoredAddress(value)) ||
        (multiple && Array.isArray(value) && value.length > 0);

    const inactiveBg = suggestionPalette?.rowInactiveBg ?? theme[themeStyle].gray_text;
    const inactiveColor = suggestionPalette?.rowInactiveColor ?? theme[themeStyle].primary_white;
    const activeBg = suggestionPalette?.rowActiveBg ?? theme[themeStyle].background_gradient.end;
    const activeColor = suggestionPalette?.rowActiveColor ?? theme[themeStyle].primary_white;
    const loadingBg = suggestionPalette?.loadingBg ?? inactiveBg;
    const loadingColor = suggestionPalette?.loadingColor ?? inactiveColor;

    return (
        <Box>
            <PlacesAutocomplete
                value={textValue}
                onChange={v => {
                    if (!v.includes('•')) {
                        setTextValue(v);
                    }
                }}
                onSelect={handleLocationSelect}>
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
                    const showSuggestionPanel = loading || suggestions.length > 0;
                    return (
                    <div style={{ position: 'relative', width: '100%' }}>
                        <TextField
                            fullWidth={fullWidth}
                            label={label}
                            placeholder={placeholder}
                            {...getInputProps()}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                endAdornment: showClearButton && (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (multiple) {
                                                setTextValue('');
                                                onChange && onChange([]);
                                                onSelect && onSelect([]);
                                            } else {
                                                clearSingle();
                                            }
                                        }}
                                        sx={{
                                            color: theme[themeStyle].placeholderGray,
                                        }}>
                                        <CloseIcon />
                                    </IconButton>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    backgroundColor: theme[themeStyle].gray_text,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                                ...(textFieldSx || {}),
                            }}
                        />
                        {showSuggestionPanel ? (
                        <div
                            className="autocomplete-dropdown-container"
                            style={{
                                position: 'absolute',
                                zIndex: 1300,
                                width: '100%',
                                marginTop: 4,
                                maxHeight: 288,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                borderRadius: suggestionPalette ? 8 : 0,
                                backgroundColor: inactiveBg,
                                border: suggestionPalette?.listBorder ?? 'none',
                                boxSizing: 'border-box',
                            }}>
                            {loading && (
                                <div
                                    style={{
                                        backgroundColor: loadingBg,
                                        padding: '10px 14px',
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: loadingColor,
                                    }}>
                                    Loading...
                                </div>
                            )}
                            {suggestions.map((suggestion, index) => {
                                const style: React.CSSProperties = {
                                    backgroundColor: suggestion.active ? activeBg : inactiveBg,
                                    color: suggestion.active ? activeColor : inactiveColor,
                                    padding: '10px 14px',
                                    cursor: 'pointer',
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: 15,
                                    lineHeight: 1.35,
                                };
                                return (
                                    <div
                                        {...getSuggestionItemProps(suggestion, {
                                            style,
                                        })}
                                        key={index}>
                                        <span>{suggestion.description}</span>
                                    </div>
                                );
                            })}
                        </div>
                        ) : null}
                    </div>
                    );
                }}
            </PlacesAutocomplete>
            {multiple && Array.isArray(value) && value.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {value.map((location, index) => (
                        <Chip
                            key={index}
                            label={location.address}
                            onDelete={() => handleRemoveLocation(index)}
                            sx={{
                                backgroundColor: theme[themeStyle].background_gradient.end,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                '& .MuiChip-deleteIcon': {
                                    color: theme[themeStyle].primary_white,
                                    '&:hover': {
                                        color: theme[themeStyle].primary_orange,
                                    },
                                },
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
